import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export interface SensitiveActionInput {
  readonly evidence?: string | undefined;
  readonly reason: string;
}

export interface SensitiveActionDialogProps {
  readonly impact: string;
  readonly onClose: () => void;
  readonly onConfirm: (input: SensitiveActionInput) => Promise<void> | void;
  readonly open: boolean;
  readonly requireEvidence: boolean;
  readonly showEvidence?: boolean;
  readonly title: string;
}

interface FormValues {
  readonly evidence: string;
  readonly reason: string;
}

const REASON_MAX_LENGTH = 2000;

export function SensitiveActionDialog({
  impact,
  onClose,
  onConfirm,
  open,
  requireEvidence,
  showEvidence = true,
  title,
}: SensitiveActionDialogProps) {
  const { t } = useTranslation();
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<FormValues>({ defaultValues: { evidence: '', reason: '' } });
  const previouslyOpen = useRef(open);
  useEffect(() => {
    if (open && !previouslyOpen.current) {
      submittingRef.current = false;
      setSubmitting(false);
      setConfirmed(false);
      setSubmitError(null);
      reset();
    }
    previouslyOpen.current = open;
  }, [open, reset]);
  const blocked = submitting || confirmed;

  const submit = handleSubmit(async (values) => {
    if (submittingRef.current || confirmed) return;

    submittingRef.current = true;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const evidence = showEvidence ? values.evidence.trim() : '';
      await onConfirm({
        evidence: evidence.length === 0 ? undefined : evidence,
        reason: values.reason.trim(),
      });
      setConfirmed(true);
      onClose();
    } catch (error) {
      setSubmitError(toSubmitError(error, t('sensitiveAction.confirmError')));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  });

  const close = () => {
    if (!blocked) onClose();
  };

  return (
    <Dialog aria-describedby="sensitive-action-impact" onClose={close} open={open}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack
          component="form"
          id="sensitive-action-form"
          noValidate
          onSubmit={submit}
          spacing={2}
          sx={{ pt: 1 }}
        >
          <Typography id="sensitive-action-impact">{impact}</Typography>
          {submitError === null ? null : <Alert severity="error">{submitError}</Alert>}
          <TextField
            autoFocus
            disabled={blocked}
            error={errors.reason !== undefined}
            helperText={errors.reason?.message}
            label={t('sensitiveAction.reason')}
            multiline
            minRows={2}
            {...register('reason', {
              maxLength: {
                value: REASON_MAX_LENGTH,
                message: t('sensitiveAction.reasonMaximum'),
              },
              required: t('sensitiveAction.reasonRequired'),
              validate: (value) => value.trim().length > 0 || t('sensitiveAction.reasonRequired'),
            })}
          />
          {showEvidence ? (
            <TextField
              disabled={blocked}
              error={errors.evidence !== undefined}
              helperText={errors.evidence?.message}
              label={
                requireEvidence
                  ? t('sensitiveAction.evidence')
                  : t('sensitiveAction.evidenceOptional')
              }
              multiline
              minRows={2}
              {...register('evidence', {
                required: requireEvidence ? t('sensitiveAction.evidenceRequired') : false,
                validate: (value) =>
                  !requireEvidence ||
                  value.trim().length > 0 ||
                  t('sensitiveAction.evidenceRequired'),
              })}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={blocked} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="error"
          disabled={blocked}
          form="sensitive-action-form"
          loading={submitting}
          type="submit"
          variant="contained"
        >
          {t('actions.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function toSubmitError(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null && 'message' in error && 'requestId' in error) {
    const candidate = error as { readonly message: unknown; readonly requestId: unknown };
    const message =
      typeof candidate.message === 'string' && candidate.message.length > 0
        ? candidate.message
        : fallback;
    return typeof candidate.requestId === 'string' && candidate.requestId.length > 0
      ? `${message} Request ID: ${candidate.requestId}`
      : message;
  }
  return fallback;
}
